// Copyright (c) 2019-24 Stax·ai, Inc.
// See LICENSE.md file in project root directory

import React from 'react';
import CreatableSelect from 'react-select/creatable';
import Select from 'react-select';

import PyeCalendarSelector from '../../Settings/PlanEditor/PlanCalendarSelect';
import api from '../../../api';
import { toast } from 'react-toastify';

import '../../../styles/investments-filters.css';
import '../../../styles/forms.css';

export default class Filter extends React.Component {

    abortController = new AbortController()

    state = {
        filters: {
            planType: [],
            planStatus: [],
            planName: [],
            internalPlanId: [],
            status: [],
            account: [],
            payee: [],
            transactions: [],
            provider: [],
            accountType: [],
            pye: { from: { endMm: 1, endDd: 1 }, to: { endMm: 12, endDd: 31 } },
            txType: [],
            txSubType: [],
            description: [],
            range: { from: null, to: null },
            settleDate: { from: { endMm: 1, endDd: 1 }, to: { endMm: 12, endDd: 31 } },
        },
        options: [],
        query: "",
        inputValue: "",
    }

    componentDidMount = () => {

        this.setState({
            filters: {
                ...this.props.filters,
                ...this.props.filterValues,
            }
        })

    }

    setPlanFilters = (key, value) => {
        // populate internalPlanId and planName based on account selection, except if the plan is already set
        if (key === "account" && !this.props?.preLoadAccounts) {

            let internalPlanIds = [];
            let planNames = [];

            const accountObjects = this.props.data?.find(a => a.account === value[value.length - 1]?.value)
            if (accountObjects) {
                internalPlanIds.push(accountObjects.internalPlanId);
                planNames.push(accountObjects.planName);
            }

            this.setState(prevState => ({
                filters: {
                    ...prevState.filters,
                    [key]: value?.map(v => v.value) || [],
                    internalPlanId: [
                        ...new Set([...prevState.filters.internalPlanId, ...internalPlanIds])
                    ],
                    planName: [
                        ...new Set([...prevState.filters.planName, ...planNames])
                    ],
                },
                inputValue: ""
            }));
        }

        // if the key is number, update the state of account as it is the actual account number state reference
        else if (key === "number") {
            this.setState(prevState => ({
                filters: {
                    ...prevState.filters,
                    account: value?.map(v => v.value) || []
                },
                inputValue: ""
            }));
        }

        // if the key is transaction, update the state of transactions as it is the actual transaction state reference
        else if (key === "transactions") {
            this.setState(prevState => ({
                filters: {
                    ...prevState.filters,
                    transactions: value
                },
                inputValue: ""
            }));
        }

        else {
            this.setState({
                filters: {
                    ...this.state.filters,
                    [key]: [
                        ...value?.map(v => v.value)
                    ]
                },
                inputValue: ""
            });
        }

        if (this.props.onFilterChange && typeof this.props.onFilterChange === 'function') {
            this.props.onFilterChange(key, value);
        }
    }

    addTransactionFilter = () => {
        const newTransaction = { value: { txType: "", operation: "", amount: "" }, label: { txType: "", operation: "", amount: "" } };
        this.setState(prevState => {
            const transactions = Array.isArray(prevState.filters.transactions)
                ? [...prevState.filters.transactions, newTransaction]
                : [newTransaction];
            return {
                filters: {
                    ...prevState.filters,
                    transactions: transactions
                }
            };
        }, () => { });
    }

    updateTransactionFilter = (key, value, index) => {
        this.setState(
            {
                filters: {
                    ...this.state.filters,
                    transactions: this.state.filters.transactions.map((t, i) => {
                        if (i === index) {
                            const updatedTransaction = {
                                value: {
                                    ...t.value,
                                    [key]: value
                                },
                                label: {
                                    ...t.label,
                                    [key]: value
                                }
                            };
                            return updatedTransaction;
                        }
                        return t;
                    })
                }
            },
            () => {
                const updatedTransactions = this.state.filters.transactions.map(t => ({
                    value: {
                        txType: t.value.txType,
                        operation: t.value.operation,
                        amount: t.value.amount
                    },
                    label: {
                        txType: t.label.txType,
                        operation: t.label.operation,
                        amount: t.label.amount
                    }
                }));
                this.setPlanFilters("transactions", updatedTransactions);
            }
        );
    }

    deleteTransactionFilter = (index) => {
        this.setState({
            filters: { ...this.state.filters, transactions: this.state.filters.transactions.filter((t, i) => i !== index) }
        })
    }

    deleteAllTransactions = () => {
        this.setState({
            filters: { ...this.state.filters, transactions: [] }
        })
    }

    editPlanDateFrom = (key, type, value) => {

        const date = {...this.state.filters[key]}; 
        date.from[type] = value; 
        this.setState({
            filters: {
                ...this.state.filters,
                [key]: date
            }
        });
    }

    editPlanDateTo = (key, type, value) => {
        const date = {...this.state.filters[key]}; 
        date.to[type] = value; 
        this.setState({
            filters: {
                ...this.state.filters,
                [key]: date
            }
        });
    }

    loadPlans = async (page = 0) => {

        // Abort the last request for loading in plans
        this.abortController.abort()
        this.abortController = new AbortController()

        // Make sure the page number is positive
        if (page < 0) page = 0;

        this.setState({
            isSearching: true
        });

        // Load plans from the API with query and pagination parameters
        const res = await api.get(`/tpa/plan/list?resultLimit=10&search=${encodeURIComponent(this.state.query)}&resultOffset=${page * 20}&filterActive=${this.state.filters?.active}`, { signal: this.abortController.signal }, false, {}, true);

        if (!res || (!res?.success && res.cancelled))
            return

        if (!res?.success) {
            toast.error("Failed to fetch TPA data.");
            this.setState({ isSearching: false });
            return;
        }

        // Set plans to state
        this.setState({
            options: res.plans,
            isSearching: false,
        });

        if (this.state.filters?.internalPlanId?.length > 0 && this.props.isSearchable && this.props?.preLoadAccounts) {
            await this.loadAccounts();
        }
    }

    loadAccounts = async (plan) => {

        // Load all the accounts based on the planId given
        this.setState({
            isSearching: true
        })

        // Abort the last request sent for loading more accounts
        this.abortController.abort();
        this.abortController = new AbortController();

        // If new plan - skip loading accounts
        if (this.state.filters?.internalPlanId?.length === 0) {
            toast.error("No plan selected");
            return this.setState({
                isSearching: false,
                accounts: []
            });
        }


        // Fetch list of accounts for the plan clicked on the Plan Editor
        const res = await api.get(`/tpa/account/list?internalPlanIds=${[...this.state.filters.internalPlanId]}`, { signal: this.abortController.signal }, false, {}, true);

        // If the request got aborted return - keep isSearching spinner
        if (!res || (!res?.success && res.cancelled))
            return

        // Account response handling
        if (!res?.success) {
            toast.error("Failed to load investment accounts");
            this.setState({
                isSearching: false
            })
            return
        }

        let accountOptions = res.accounts;
        accountOptions = accountOptions?.map(a => ({ account: a.number, payee: a.payee, provider: a.provider }));

        // Set accounts and maxAccountsCount
        this.setState({
            isSearching: false,
            options: accountOptions,
        })
    }

    search = async (query, key) => {
        this.setState({ query });

        // If we've already set a timeout, clear it and overwrite
        if (this.searchTimeout)
            clearTimeout(this.searchTimeout);

        this.searchTimeout = setTimeout(async () => {
            // if (key === 'account')
            //     await this.loadAccounts();
            // else 
            await this.loadPlans();
        }, 50);
    }

    resetDescriptionSearch = () => {
        this.setPlanFilters("description", []);
    }

    renderConfigField = (c, idx) => {
        switch (c.dataType) {
            case "creatableSelect":
                return (
                    <div key={idx} style={{ width: '100%', marginBottom: 40 }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <label>{c.label}</label>
                            <div
                                className='a'
                                style={{ paddingRight: 10 }}
                                onClick={() => {
                                    this.setState({ filters: { ...this.state.filters, [c.key]: [] } });
                                }}
                            >
                                Clear
                            </div>
                        </div>
                        {
                            c.key === "description" ?
                                <CreatableSelect
                                    className={"filter-select-container"}
                                    classNamePrefix={"multi-select"}
                                    isMulti={true}
                                    isClearable={false}
                                    autoBlur={false}
                                    components={c.key === "description" ? {
                                        Option: (props) => {
                                            return (
                                                <div
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => this.setPlanFilters(c.key, [...(this.state.filters?.description ? this.state.filters?.description?.map(v => ({ value: v, label: v })) : []), { value: props.data.value }])
                                                    }
                                                >
                                                    <div className={"multi-select__option multi-select__option--is-focused css-1n7v3ny-option"}>{props.data.label}</div>
                                                </div>
                                            )
                                        }
                                    }
                                        : {}
                                    }
                                    options={
                                        c?.preSetOptions ?
                                            c.preSetOptions :
                                            this.props.isSearchable ?
                                                this.state?.options?.map(p => ({ value: p[c.key], label: p[c.key] })) ?
                                                    this.state?.options?.map(p => ({ value: p[c.key], label: p[c.key] })) :
                                                    [] :
                                                this.props.data?.map(a => ({ value: a[c.key], label: a[c.key] }))
                                                    .filter(v => v.value)
                                                    .reduce((acc, current) => {
                                                        if (!acc.find(item => item.value === current.value)) {
                                                            acc.push(current);
                                                        }
                                                        return acc;
                                                    }, [])
                                    }
                                    onChange={(value, actionMeta) => {
                                        if (c.key !== "description")
                                            this.setPlanFilters(c.key, value ? value : []);

                                        else if (actionMeta.action === 'remove-value') {
                                            // if a value is removed, update the description array with the new value
                                            this.setState({
                                                filters: {
                                                    ...this.state.filters,
                                                    description: value?.map(v => v.value)
                                                }
                                            })
                                        }
                                    }}
                                    value={
                                        this.state.filters?.[c.key]?.length > 0 ?
                                            this.state.filters?.[c.key].map(v => ({ value: v, label: v })) :
                                            c.key === "number" ? this.state.filters?.account?.map(v => ({ value: v, label: v })) :
                                                []
                                    }
                                    onInputChange={(inputValue, actionMeta) => {
                                        if (actionMeta.action === 'input-change') {
                                            this.setState({ inputValue });
                                        }
                                        if (this.props.isSearchable) {
                                            this.search(inputValue, c.key);
                                        }
                                    }}
                                    noOptionsMessage={() => "Start typing to search..."}
                                    isLoading={this.state.isSearching}
                                    isValidNewOption={() => false}
                                    onKeyDown={async (event) => {
                                        if (c.key === "description" && event.key === "Enter") {
                                            const inputValue = event.target.value;
                                            // If the input value is empty, do nothing
                                            if (!inputValue) {
                                                return;
                                            }
                                            this.setPlanFilters(
                                                c.key,
                                                [
                                                    ...(this.state.filters?.description ? this.state.filters?.description?.map(v => ({ value: v, label: v })) : []),
                                                    { value: inputValue, label: inputValue }
                                                ]
                                            );
                                        }
                                    }}
                                    inputValue={c.key === "description" && this.state.inputValue}
                                />
                                :
                                <CreatableSelect
                                    className={"filter-select-container"}
                                    classNamePrefix={"multi-select"}
                                    isMulti={true}
                                    isClearable={false}
                                    autoBlur={false}
                                    options={
                                        c?.preSetOptions ?
                                            c.preSetOptions :
                                            this.props.isSearchable ?
                                    this.state?.options?.map(p => ({ value: p[c.key], label: p[c.key] }))
                                        .filter((option, index, self) =>
                                            index === self.findIndex((o) => o.value === option.value)) || []: 
                                                this.props.data?.map(a => ({ value: a[c.key], label: a[c.key] }))
                                                    .filter(v => v.value)
                                                    .reduce((acc, current) => {
                                                        if (!acc.find(item => item.value === current.value)) {
                                                            acc.push(current);
                                                        }
                                                        return acc;
                                                    }, [])
                                    }
                                    onChange={value => this.setPlanFilters(c.key, value ? value : [])}
                                    value={
                                        this.state.filters?.[c.key]?.length > 0 ?
                                            this.state.filters?.[c.key].map(v => ({ value: v, label: v })) :
                                            c.key === "number" ? this.state.filters?.account?.map(v => ({ value: v, label: v })) :
                                                []
                                    }
                                    onInputChange={(inputValue) => {
                                        this.props.isSearchable && this.search(inputValue, c.key);
                                    }}
                                    noOptionsMessage={() => "Start typing to search..."}
                                    isLoading={this.state.isSearching}
                                    isValidNewOption={() => false}
                                />
                        }

                    </div>
                );
            case "transaction":

                if (this.mounted && !this.state.filters?.transactions?.[idx]?.value && !this.state.filters?.transactions?.[idx]?.label) {
                    const transactions = [...this.state.filters.transactions];
                    transactions[idx] = {
                        value: {
                            txType: this.state.filters?.transactions?.[idx]?.txType,
                            operation: this.state.filters?.transactions?.[idx]?.operation,
                            amount: this.state.filters?.transactions?.[idx]?.amount
                        },
                        label: {
                            txType: this.state.filters?.transactions?.[idx]?.txType,
                            operation: this.state.filters?.transactions?.[idx]?.operation,
                            amount: this.state.filters?.transactions?.[idx]?.amount
                        }
                    };
                    this.setState(prevState => ({
                        filters: { ...prevState.filters, transactions }
                    }));
                }

                return (
                    <div key={idx} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: 14, marginBottom: 14 }}>

                        <Select
                            className="filter-selector-container"
                            classNamePrefix='filter-selector'
                            value={{
                                value: this.state.filters?.transactions?.[idx]?.value?.txType ?? this.state.filters?.transactions?.[idx]?.txType ?? "",
                                label: this.state.filters?.transactions?.[idx]?.label?.txType ?? this.state.filters?.transactions?.[idx]?.txType ?? ""
                            }}
                            options={this.props.filterComponents.find(f => f.dataType === 'transaction').typeOptions.map(elem => ({
                                value: elem,
                                label: elem
                            }))}
                            onChange={(e) => this.updateTransactionFilter('txType', e.value, idx)}
                        />
                        <Select
                            className="filter-selector-container"
                            classNamePrefix='filter-selector'
                            value={{
                                value: this.state.filters?.transactions?.[idx]?.value?.operation ?? this.state.filters?.transactions?.[idx]?.operation ?? "",
                                label: this.state.filters?.transactions?.[idx]?.label?.operation ?? this.state.filters?.transactions?.[idx]?.operation ?? ""
                            }}
                            options={this.props.filterComponents.find(f => f.dataType === 'transaction').operationOptions.map(elem => ({
                                value: elem,
                                label: elem
                            }))}
                            onChange={(e) => this.updateTransactionFilter('operation', e.value, idx)}
                        />
                        <input
                            className="filter-select-edit"
                            type={"text"}
                            value={this.state.filters?.transactions?.[idx]?.value?.amount ?? this.state.filters?.transactions?.[idx]?.amount ?? ""}
                            onChange={(e) => this.updateTransactionFilter('amount', e.target.value, idx)}
                            placeholder={"Amount..."}
                            onKeyDown={(e) => { }}
                        />
                        <div className="filter-delete-transaction"
                            onClick={() => this.deleteTransactionFilter(idx)}
                            style={{
                                color: "var(--color-accent-dark)",
                                backgroundImage: `url(https://static.stax.ai/images/icons/delete.png)`,
                                backgroundRepeat: 'no-repeat',
                                cursor: 'pointer',
                                backgroundSize: 20,
                            }}>
                        </div>
                    </div>
                );
            case "calendar":
                return (
                    <div
                        className="plan-editor-information-pye"
                        style={{ gridColumnEnd: 1, width: '100%' }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <label>{c.label}</label>
                            <div className='a' style={{ paddingRight: 10 }} onClick={() => {
                                const pye = { ...this.state.filters[c.key] };
                                pye.to.endDd = 31;
                                pye.to.endMm = 12;
                                pye.from.endDd = 1;
                                pye.from.endMm = 1;
                                this.setState({
                                    filters: {
                                        ...this.state.filters,
                                        pye
                                    }
                                });
                            }}
                            >Reset</div>
                        </div>
                        <div
                            style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}
                        >
                            <PyeCalendarSelector
                                style={(c?.style?.width ? { width: c.style.width } : {})}
                                updateMm={(type, value) => { this.editPlanDateFrom(c.key, type, value) }}
                                updateDd={(type, value) => { this.editPlanDateFrom(c.key, type, value) }}
                                endMm={this.state.filters[c.key]?.from?.endMm}
                                endDd={this.state.filters[c.key]?.from?.endDd}
                                onChange={(value) => { }}
                            />
                            <span style={{ margin: 10 }}>to</span>
                            <PyeCalendarSelector
                                style={(c?.style?.width ? { width: c.style.width } : {})}
                                updateMm={(type, value) => { this.editPlanDateTo(c.key, type, value) }}
                                updateDd={(type, value) => { this.editPlanDateTo(c.key, type, value) }}
                                endMm={this.state.filters?.[c.key]?.to?.endMm}
                                endDd={this.state.filters?.[c.key]?.to?.endDd}
                                onChange={(value) => { }}
                            />
                        </div>

                    </div>
                );
            case "amountRange":
                return (
                    <div style={{ width: '100%' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <label>{c.label}</label>
                            <div
                                className='a'
                                style={{ paddingRight: 10 }}
                                onClick={() => {
                                    this.setState({ filters: { ...this.state.filters, range: { from: null, to: null } } });
                                }}
                            >
                                Clear
                            </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', marginBottom: 40 }}>
                            <input
                                className="filter-select-edit"
                                type={"text"}
                                value={this.state.filters?.range?.from ?? ""}
                                onChange={(e) => {
                                    const value = e.target.value;

                                    // Allow the value if it's numeric (including negative numbers) or just a minus sign
                                    if (!isNaN(Number(value)) || value === '-') {
                                        // Update the state with the new value, converting it to a number if it's not just a "-"
                                        this.setState(prevState => ({
                                            filters: {
                                                ...prevState.filters,
                                                range: {
                                                    ...prevState.filters.range,
                                                    from: value === '-' ? '-' : Number(value)
                                                }
                                            }
                                        }));
                                    } else {
                                        // If the value is not numeric and not a minus sign, reset the 'from' value
                                        this.setState(prevState => ({
                                            filters: {
                                                ...prevState.filters,
                                                range: {
                                                    ...prevState.filters.range,
                                                    from: null
                                                }
                                            }
                                        }));
                                    }
                                }}
                                placeholder={"Greater than or equal to..."}
                                onKeyDown={(e) => { }}
                            />
                            <span style={{ margin: 10 }}>to</span>
                            <input
                                className="filter-select-edit"
                                type={"text"}
                                value={this.state.filters?.range?.to ?? ""}
                                onChange={(e) => {
                                    // if the value is not convertible into a number
                                    if (isNaN(Number(e.target.value))) {
                                        this.setState(prevState => ({
                                            filters: {
                                                ...prevState.filters,
                                                range: {
                                                    ...prevState.filters?.range,
                                                    to: null // Update the `from` value with the new value
                                                }
                                            }
                                        }));
                                        return;
                                    }
                                    const inputValue = Number(e.target.value);
                                    this.setState(prevState => ({
                                        filters: {
                                            ...prevState.filters,
                                            range: {
                                                ...prevState.filters?.range,
                                                to: inputValue // Update the `from` value with the new value
                                            }
                                        }
                                    }));
                                }}
                                onKeyDown={(e) => { }}
                                placeholder={"Less than or equal to..."}
                            />
                        </div>
                    </div>

                );
            case "select":
                return (
                    <div style={{ width: '100%', marginBottom: 40 }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <label>{c.label}</label>
                            <div
                                className='a'
                                style={{ paddingRight: 10 }}
                                onClick={() => {
                                    this.setState({ filters: { ...this.state.filters, [c.key]: [] } });
                                }}
                            >
                                Clear
                            </div>
                        </div>
                        <Select
                            isMulti={c.isMulti}
                            className={"filter-select-container"}
                            classNamePrefix={c.isMulti ? "multi-select" : 'filter-select'}
                            options={c.options}
                            onChange={value => this.setPlanFilters(c?.key, value ?? [])}
                            value={
                                this.state.filters?.[c.key]?.length > 0 ?
                                    c.options.filter(o => this.state.filters?.[c.key].includes(o.value))
                                    :
                                    []
                            }
                        />
                    </div>
                )
            default: // String or secret
                return (<input
                    type={c.dataType === "secret" ? "password" : "text"}
                    className="config-field-string"
                    value={this.state.config.length > idx ? this.state.config[idx].value : c.default ? c.default : ""}
                    onChange={e => this.setConfigValue(idx, e.target.value)}
                />);
        }
    }

    render = () => {
        // const d = this.props.data ? this.props.data : {};
        return (
            <>
                <>
                    <h1 className='filter-plans-title override'>{this.props.title}</h1>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', ...(this.props.style?.width ? { width: this.props.style.width } : { width: 500 }), }}>
                        <div
                            className='a'
                            style={{ paddingRight: 10 }}
                            onClick={() => {
                                this.props.clearFilters();
                                this.setState(prevState => ({
                                    filters: {
                                        planType: [],
                                        planStatus: [],
                                        planName: [],
                                        internalPlanId: [],
                                        status: [],
                                        account: [],
                                        payee: [],
                                        transactions: [],
                                        provider: [],
                                        accountType: [],
                                        pye: { from: { endMm: 1, endDd: 1 }, to: { endMm: 12, endDd: 31 } },
                                        txType: [],
                                        txSubType: [],
                                        description: [],
                                        range: { from: null, to: null },
                                        settleDate: { from: { endMm: 1, endDd: 1 }, to: { endMm: 12, endDd: 31 } },
                                    }
                                }));

                            }}
                        >
                            Clear all filters
                        </div>
                    </div>
                </>

                <div
                    style={{
                        ...(this.props.style?.width ? { width: this.props.style.width } : { width: 500 }),
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',

                    }}
                >
                    {
                        this.props.filterComponents?.map((f, idx) => {
                            if (f.dataType === "transaction") return null;
                            return this.renderConfigField(f, idx);
                        })
                    }

                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', flexDirection: 'column' }}>

                        {this.props.includeTransaction &&
                            <>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                                    <label>Filter by balance or transactions</label>
                                    <div className='a' style={{ paddingRight: 10 }} onClick={() => this.deleteAllTransactions()}>Clear</div>
                                </div>
                                {
                                    // handling transactions separately as the rendering is more complex...
                                    this.state.filters?.transactions?.length > 0 ?
                                        this.state.filters?.transactions?.map((transaction, index) => {
                                            return this.renderConfigField(this.props.filterComponents.find(f => f.dataType === 'transaction'), index);
                                        })
                                        : ""
                                }
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                                    <div
                                        className='title-icon'
                                        style={{ color: '#0091BD', width: 'auto', fontSize: 'medium', marginLeft: 0 }}
                                        onClick={() => this.addTransactionFilter()}
                                    >
                                        Add a filter
                                    </div>
                                </div>
                            </>
                        }
                        {
                            this.props?.renderFooterButtons === false ? "" :
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', marginTop: 50 }}>
                                    <div className='a' onClick={() => { this.props.modal(); }}>← Discard changes</div>
                                    <input
                                        type='submit'
                                        value='Apply Filters'
                                        className='filter-inline-submit'
                                        onClick={() => {
                                            this.props.setFilter({ ...this.state.filters });
                                            this.props.modal();
                                        }}
                                    />
                                </div>
                        }


                    </div>
                </div>
            </>

        );
    }

};